import React, { useRef, useState, useEffect, useCallback } from "react";

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/Input";
import { SelectedValueBadge } from "./SelectedValueBadge";

interface TokenizedTextInputProps {
  placeholder?: string;
  value: string[];
  onUpdate: (val: string[]) => void;
  inputFilter?: (val: string) => string;
  tokenIsValid?: (val: string) => boolean;
  labeledBy?: string;
}

export function TokenizedTextInput({
  placeholder,
  value: tokens,
  onUpdate,
  inputFilter = (val: string) => val,
  tokenIsValid = () => true,
  labeledBy,
}: TokenizedTextInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [focused, setFocused] = useState<boolean>(false);
  const [text, setText] = useState<string>("");
  const onRemove = useCallback(
    (val: string) => {
      onUpdate(tokens.filter((tok) => tok !== val));
    },
    [onUpdate, tokens],
  );

  useEffect(() => {
    setText(tokens.join(", "));
  }, [tokens]);

  return (
    <div className="relative">
      <Input
        onBlur={() => {
          setFocused(false);
          // split input text into tokens
          const tokens = text.split(/[,\s]+/).filter((token) => "" !== token);

          // remove dups and update
          onUpdate([...new Set(tokens)]);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        type="text"
        value={text}
        aria-labelledby={labeledBy}
        className="min-h-9.5"
        onChange={(e) => setText(inputFilter(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            inputRef.current?.blur();
          }
        }}
        ref={inputRef}
        data-lpignore={true}
      />
      <div
        tabIndex={0}
        onFocus={() => {
          setFocused(true);
          inputRef.current?.focus();
        }}
        data-testid="tokenized-text-input-display"
        className={cn(
          "relative top-0 -mt-9.5 flex min-h-9.5 w-full min-w-0 flex-wrap gap-2 rounded-md border border-input bg-background px-3 py-2 text-base transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          "leading-5 text-muted-foreground",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          { hidden: focused },
        )}
      >
        {tokens.length === 0 && <span>{placeholder}</span>}
        {tokens.map((value, i) => {
          return (
            <SelectedValueBadge
              key={i}
              label={value}
              value={value}
              isValid={tokenIsValid(value)}
              onRemove={onRemove}
            ></SelectedValueBadge>
          );
        })}
      </div>
    </div>
  );
}
